/**
 * RDV en ligne (#429) — "demande de RDV" contract (Q3, Q4, Q5).
 *
 * Reduced V1 shape: statuses are `pending | accepted | refused` only.
 * The counter-proposition machinery (`counter_proposed` status, counter slots,
 * parent link, public token) is PARKED (#2) and, if kept, is purely additive.
 *
 * Every demande carries a chosen slot (`requestedStartAt`, NOT NULL — Q5): the
 * no-slot cases (address not found / out of zone / no availability) show a
 * phone CTA and persist nothing.
 *
 * See `docs/spec/rdv-en-ligne/adr-moteur-et-contrats.md`.
 */
import { z } from "zod";
import { FR_ZIP_MESSAGE, FR_ZIP_REGEX } from "../geo/frZip.js";
import { GeocodePrecisionSchema, GeocodeSourceSchema } from "../geo/geocode.model.js";
import { PUBLIC_API_ADDRESS_MAX, PUBLIC_API_EMAIL_MAX, PUBLIC_API_REQUESTED_START_AT_MAX, PUBLIC_API_SERVICE_IDS_MAX, PUBLIC_API_SHORT_STRING_MAX, } from "../publicApi/bounds.js";
import { buildServiceLinesSchema } from "./serviceLines.js";
export const RDV_REQUEST_COMMENT_MAX = 1000;
export const RdvRequestCommentSchema = z
    .string()
    .max(RDV_REQUEST_COMMENT_MAX, "Le commentaire ne doit pas dépasser 1 000 caractères");
export const RDV_REQUEST_STATUSES = ["pending", "accepted", "refused"];
/**
 * Public submission payload, created ONLY at final validation (FR29, NFR5).
 * The target company is resolved from the public slug at the route level.
 *
 * Contact + service-address fields are optional here: when the client is
 * recognized by email (FR21b), the route reuses the stored fiche and the public
 * flow omits those steps; otherwise the service enforces their presence per
 * path (Q3). Geocoding (score >= SCORE_LIMIT) is validated server-side (FR22).
 */
/**
 * The chosen slot start, as emitted by `/proposals` (`RdvPublicSlot.start`): the
 * planning engine works in NAIVE Europe/Paris wall-clock, so its slots are
 * offset-less ISO exactly `YYYY-MM-DDTHH:mm:ss` (see `engine.ts`, `proposals.ts`).
 * Zod 3.22's `.datetime()` REQUIRES a `Z`/offset and would 400 that exact value —
 * breaking proposals → submit. So we validate the naive wall-clock ourselves.
 */
const WALLCLOCK_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;
/**
 * True iff `value` is a real `YYYY-MM-DDTHH:mm:ss` wall-clock — the exact format
 * `/proposals` emits. The component round-trip (via `Date.UTC`, a pure calendar
 * check free of local-tz/DST noise) rejects BOTH impossible values
 * (`2026-13-40T99:99:99` → `Invalid Date`) AND shape-valid rollovers
 * (`2026-02-30T09:00:00`, which `new Date` would silently normalize to Mar 2 and
 * persist as a DIFFERENT slot than the client picked).
 */
function isRealWallClockDatetime(value) {
    const match = WALLCLOCK_DATETIME.exec(value);
    if (match == null) {
        return false;
    }
    // Defaults keep each capture `string` (the 6 groups are mandatory in the regex,
    // so always present — the defaults only satisfy the strict indexing type).
    const [, y = "", mo = "", d = "", h = "", mi = "", s = ""] = match;
    const year = Number(y);
    const month = Number(mo);
    const day = Number(d);
    const hour = Number(h);
    const minute = Number(mi);
    const second = Number(s);
    const utc = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    return (utc.getUTCFullYear() === year &&
        utc.getUTCMonth() === month - 1 &&
        utc.getUTCDate() === day &&
        utc.getUTCHours() === hour &&
        utc.getUTCMinutes() === minute &&
        utc.getUTCSeconds() === second);
}
/**
 * The service basket (#711) is `services: {serviceDescriptionId, quantity}[]`
 * (required — see `buildServiceLinesSchema`, #724 dropped the legacy
 * `serviceDescriptionIds: string[]` shape once the FE migration fully rolled out).
 */
export const CreateRdvRequestPayloadSchema = buildServiceLinesSchema({
    // chosen slot start (NOT NULL, Q5) — the naive Paris wall-clock echoed from /proposals.
    requestedStartAt: z
        .string()
        .max(PUBLIC_API_REQUESTED_START_AT_MAX)
        .refine(isRealWallClockDatetime, {
        message: "Invalid Europe/Paris wall-clock datetime (expected YYYY-MM-DDTHH:mm:ss)",
    }),
    email: z.string().email().max(PUBLIC_API_EMAIL_MAX), // entry point (FR21a) — always present
    // contact (individual: first/last/phone; pro: companyName/siret) — non-recognized only
    comment: RdvRequestCommentSchema.optional(),
    firstName: z.string().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    lastName: z.string().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    phone: z.string().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    customerType: z.enum(["individual", "professional"]).optional(),
    companyName: z.string().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    siret: z.string().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    // geocoded service address (FR22) — non-recognized only
    serviceAddress: z.string().max(PUBLIC_API_ADDRESS_MAX).optional(),
    serviceLat: z.number().finite().min(-90).max(90).optional(),
    serviceLng: z.number().finite().min(-180).max(180).optional(),
    serviceGeocodeScore: z.number().finite().min(0).max(1).optional(),
    /**
     * Granularité + label du géocodage (#807) : le tunnel renvoie ce que
     * `POST /api/pro/:slug/geocode` lui a déjà donné — un aller-retour, pas une
     * divulgation nouvelle sur la surface publique.
     * Tous deux `optional()`, et pas seulement par symétrie avec le reste de
     * l'adresse : un bundle antérieur au déploiement ne les porte pas, et les
     * exiger le ferait échouer en fin de tunnel.
     */
    serviceGeocodePrecision: GeocodePrecisionSchema.optional(),
    serviceGeocodeLabel: z.string().trim().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    // Structured zip/city (#661) — the tunnel's address form collects them
    // separately; forwarded so the demande keeps them structured instead of
    // only the flattened `serviceAddress` label.
    serviceZip: z.string().regex(FR_ZIP_REGEX, FR_ZIP_MESSAGE).optional(),
    serviceCity: z.string().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    /**
     * Porte de sortie « je place le point moi-même » (#799) : `"manual"` fait
     * foi du pin posé par le visiteur — refinement ci-dessous, jamais sans
     * lat/lng. Absent du payload → comportement inchangé (bundle antérieur au
     * déploiement, même rétro-compat que `serviceGeocodePrecision`).
     */
    serviceGeocodeSource: GeocodeSourceSchema.optional(),
    // optional different billing address (FR28)
    billingAddress: z.string().max(PUBLIC_API_ADDRESS_MAX).optional(),
    /**
     * Structured billing address complement/zip/city (#1240) — mirrors the
     * structured `serviceZip`/`serviceCity` above. All optional: a caller may
     * still send only the flat `billingAddress` label.
     */
    billingAddress2: z.string().trim().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
    billingZip: z.string().regex(FR_ZIP_REGEX, FR_ZIP_MESSAGE).optional(),
    billingCity: z.string().trim().max(PUBLIC_API_SHORT_STRING_MAX).optional(),
}, { maxLines: PUBLIC_API_SERVICE_IDS_MAX }).superRefine((data, ctx) => {
    if (data.serviceGeocodeSource === "manual" &&
        (data.serviceLat == null || data.serviceLng == null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "A manual geocode source requires serviceLat and serviceLng",
            path: ["serviceLat"],
        });
    }
});
/** Refusal payload (FR38 — dry refusal, motif mandatory; a blank motif is rejected too). */
export const RefuseRdvRequestPayloadSchema = z.object({
    refusalReason: z.string().trim().min(1),
});
/**
 * Acceptance payload (Story 4.3, FR36/FR40) — manager-scoped `POST
 * /api/rdv-requests/:id/accept`. The pro validates a fully editable, prefilled
 * form; only on save is the fiche created/updated and a standard MyTour `Event`
 * created.
 *
 * The client-resolution `mode` echoes the pro's decision on the live 3-case
 * identification (Story 4.2, `RdvCustomerIdentification`):
 * - `existing` → the picked fiche id is updated (its service address is
 *   overwritten with the form address, Q8 — "l'adresse de la demande gagne");
 * - `new` → a fiche is created from the demande's stored contact + the form
 *   address (the contact is NOT re-sent here: it lives on the demande and is not
 *   editable in the acceptance form — only the address, prestations and slot are).
 *
 * The service address is STRUCTURED (address/zip/city), reusing the standard
 * customer create/edit path: the demande stores a flat `serviceAddressRaw` plus
 * (possibly null, #661) structured `serviceZip`/`serviceCity` — a `Customer`
 * needs both NOT NULL, so the form prefills them when the demande has them and
 * the pro completes them otherwise (older demande, or an edge case where the
 * source had none). Les coordonnées ne font PAS partie de ce payload — mais
 * le backend peut quand même faire passer le pin du visiteur quand il est
 * jugé fiable (`RdvAcceptService.resolveManualPinFields`, #799), le seul
 * chemin où une coordonnée FE périmée/forgée pourrait atteindre la fiche ;
 * tous les autres cas re-géocodent côté serveur depuis l'adresse structurée,
 * comme toute autre écriture de fiche client.
 *
 * `requestedStartAt` is editable and NOT necessarily the demande's slot: a past
 * requested slot is not prefilled (Q6), the pro picks a new date.
 *
 * The confirmed-prestations basket is `services: {serviceDescriptionId,
 * quantity}[]` (`buildServiceLinesSchema`, required — #724 dropped the legacy
 * `serviceDescriptionIds: string[]` shape). No `maxLines` here (unchanged
 * from before #711 — this is a manager-scoped, catalog-bounded basket). The
 * uniqueness refine that used to live inline now lives in the shared builder,
 * still keyed on `serviceDescriptionId`: a duplicated prestation would create a
 * duplicate plannedService and double the derived duration — quantity is the
 * correct way to book the same prestation more than once, never id repetition.
 * `RdvAcceptService` only ever trusts the snapshot's quantity, never the wire
 * value (same posture as duration/label) — see `rdvAccept.service.ts`.
 */
export const AcceptRdvRequestPayloadSchema = buildServiceLinesSchema({
    customerResolution: z.discriminatedUnion("mode", [
        z.object({ mode: z.literal("existing"), customerId: z.string().min(1) }),
        z.object({ mode: z.literal("new") }),
    ]),
    // Editable slot start — the naive Paris wall-clock, same frame as the whole module.
    requestedStartAt: z.string().refine(isRealWallClockDatetime, {
        message: "Invalid Europe/Paris wall-clock datetime (expected YYYY-MM-DDTHH:mm:ss)",
    }),
    // Structured service address written to the fiche (Q8, editable).
    serviceAddress: z.string().trim().min(1),
    serviceAddress2: z.string().trim().optional(),
    serviceZip: z.string().trim().regex(FR_ZIP_REGEX, FR_ZIP_MESSAGE),
    serviceCity: z.string().trim().min(1),
});
//# sourceMappingURL=request.types.js.map